/**
 * @file: useTrackVisibleOrders.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 */

import { TaskListItem } from '@euler/model/TaskListItem';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { ViewToken } from 'react-native';
import { ReportViewSummaryStore } from './ReportViewSummaryStore';

/** cards loaded on each side of the visible range, so scrolling stays smooth. */
const kPrefetchCount = 2;

/**
 * Feeds the on-screen (plus a small buffer) order numbers to the store.
 * Returns the `viewabilityConfigCallbackPairs` prop for the list — a stable
 * value, since react-native rejects changing it after mount.
 */
export const useTrackVisibleOrders = (
  store: ReportViewSummaryStore,
  data: TaskListItem[],
  enabled: boolean,
) => {
  const onViewableItemsChanged = usePersistFn(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (!enabled) return;
      const indices = viewableItems
        .map(token => token.index)
        .filter((index): index is number => index != null);
      if (!indices.length) return;
      const from = Math.max(0, Math.min(...indices) - kPrefetchCount);
      const to = Math.min(
        data.length - 1,
        Math.max(...indices) + kPrefetchCount,
      );
      const orderNos: string[] = [];
      for (let i = from; i <= to; i++) {
        const orderNo = data[i]?.orderNo;
        if (orderNo) orderNos.push(orderNo);
      }
      store.setVisible(orderNos);
    },
  );

  return useCreation(
    () => [
      {
        // NOTE: a threshold of 0 marks EVERY rendered cell viewable
        // (`percent >= 0`), which would defeat the point — 1 means "any
        // sliver of the card is on screen".
        viewabilityConfig: { itemVisiblePercentThreshold: 1 },
        onViewableItemsChanged,
      },
    ],
    [onViewableItemsChanged],
  );
};
